import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Avatar,
  Typography,
  Button,
  Box,
  useTheme,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { Add as AddIcon,  } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch } from '@/store';
import { followUser, unfollowUser, cancelFollowRequest } from '@/store';
import { UserSuggestion, UserSearchResult } from '../../services';

interface ProfileCardProps {
  user: UserSuggestion | UserSearchResult;
  variant?: 'suggestion' | 'search';
  onAccept?: () => void;
  onReject?: () => void;
  showAcceptReject?: boolean;
  refresh?:()=>void;
}

export const ProfileCard: React.FC<ProfileCardProps> = ({
  user,
  variant = 'suggestion',
  onAccept,
  onReject,
  showAcceptReject = false,
  refresh
}) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const isSearchResult = (user: UserSuggestion | UserSearchResult): user is UserSearchResult => {
    return 'isFollowing' in user;
  };

  const [localRequestPending, setLocalRequestPending] = useState(false);
  const [localIsFollowing, setLocalIsFollowing] = useState(isSearchResult(user) ? user.isFollowing : false);

  const handleFollow = async () => {
    if (isSearchResult(user) && (localIsFollowing || user.isFollowing)) {
      // Unfollow user
      setLocalIsFollowing(false);
     await dispatch(unfollowUser(user._id)).unwrap();
      if(refresh){
    refresh();
    }
    } else {
      // Follow user
      setLocalRequestPending(true);
      await dispatch(followUser({ recipientId: user._id })).unwrap();
       if(refresh){
    refresh();
    }
    }
  };

  const handleCancelRequest = async () => {
    setLocalRequestPending(false);
    await dispatch(cancelFollowRequest({ recipientId: user._id })).unwrap();
    if(refresh){
    refresh();
    }
  };

  // const handleMessage = () => {
  //   // TODO: Implement messaging functionality
  // };

  const handleProfileClick = () => {
    navigate(`/profile/${user._id}`);
  };

  return (
    <Card
      elevation={0}
      onClick={handleProfileClick}
      sx={{
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: 'background.paper',
        transition: 'all 0.2s ease',
        cursor: 'pointer',
        height: '100%',
        minHeight: 180,
        display: 'flex',
        flexDirection: 'column',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: theme.shadows[4],
        },
      }}
    >
      <CardContent sx={{ p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
        <List sx={{ width: '100%', bgcolor: 'background.paper', p: 0, flex: 1, display: 'flex', flexDirection: 'column' }}>
          <ListItem
            alignItems="flex-start"
            sx={{
              flexDirection: 'column',
              px: 2,
              py: 2,
              flex: 1,
              display: 'flex',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ display: 'flex', width: '100%', gap: 2 }}>
              <ListItemAvatar>
                {user.isLookingForJob ? (
                  <Box sx={{ position: 'relative', display: 'inline-block' }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -4,
                        left: -4,
                        right: -4,
                        bottom: -4,
                        borderRadius: '50%',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        padding: '4px',
                      }}
                    >
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          borderRadius: '50%',
                          background: theme.palette.background.paper,
                        }}
                      />
                    </Box>
                    <Avatar
                      src={user.profilePictureUrl || undefined}
                      sx={{
                              width: 56,
                      height: 56,
                      bgcolor: 'primary.main',
                      fontSize: '1.25rem',
                        // width: 56,
                        // height: 56,
                        // bgcolor: 'primary.main',
                        // fontSize: '1.25rem',
                        // position: 'relative',
                        // zIndex: 1,
                      }}
                    >
                      {user?.name?.charAt(0)?.toUpperCase()}
                    </Avatar>
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -8,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                        color: 'white',
                        fontSize: '7px',
                        fontWeight: 700,
                        padding: '2px 8px',
                        borderRadius: '10px',
                        whiteSpace: 'nowrap',
                        zIndex: 2,
                        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                      }}
                    >
                      OPEN TO WORK
                    </Box>
                  </Box>
                ) : (
                  <Avatar
                    src={user.profilePictureUrl || undefined}
                    sx={{
                      width: 56,
                      height: 56,
                      bgcolor: 'primary.main',
                      fontSize: '1.25rem',
                    }}
                  >
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </Avatar>
                )}
              </ListItemAvatar>
              <ListItemText
                primary={
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      color: 'text.primary',
                      fontSize:14,
                           overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                    }}
                  >
                    {user.name}
                  </Typography>
                }
                secondary={
                  <React.Fragment>
                    {user?.aboutMe && (
                      <Tooltip title={user.aboutMe} arrow placement="bottom">
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            color: 'text.secondary',
                            display: '-webkit-box',
                            fontSize: 11,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                            cursor: 'pointer',
                          }}
                        >
                          {user.aboutMe}
                        </Typography>
                      </Tooltip>
                    )}
                    {!user?.aboutMe && variant === 'search' && (
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{      color: 'text.secondary',
                            display: '-webkit-box',
                            fontSize: 12,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            lineHeight: 1.5,
                            cursor: 'pointer',}}
                      >
                        {user.email}
                      </Typography>
                    )}
                  </React.Fragment>
                }
              />
            </Box>

            <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 2, width: '100%' }}>
              {showAcceptReject ? (
            <>
              <Button
                variant="contained"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onAccept?.();
                }}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  py: 0.75,
                  bgcolor: 'success.main',
                  '&:hover': {
                    bgcolor: 'success.dark',
                  },
                }}
              >
                Accept
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onReject?.();
                }}
                sx={{
                  flex: 1,
                  borderRadius: 2,
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  py: 0.75,
                  borderColor: 'error.main',
                  color: 'error.main',
                  '&:hover': {
                    borderColor: 'error.dark',
                    bgcolor: 'error.light',
                  },
                }}
              >
                Reject
              </Button>
            </>
          ) : variant === 'search' && isSearchResult(user) ? (
            <>
              {localIsFollowing || user.isFollowing ? (
                <>
                  {/* <Button
                    variant="outlined"
                    size="small"
                    startIcon={<PersonIcon sx={{ fontSize: 16 }} />}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleMessage();
                    }}
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      py: 0.75,
                      mr: 0.5,
                    }}
                  >
                    Message
                  </Button> */}
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleFollow();
                    }}
                    sx={{
                      flex: 1,
                      borderRadius: 2,
                      fontSize: '0.75rem',
                      textTransform: 'none',
                      py: 0.75,
                      ml: 0.5,
                    }}
                  >
                    Unfollow
                  </Button>
                </>
              ) : localRequestPending || user.hasRequestPending ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCancelRequest();
                  }}
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    py: 0.75,
                    borderColor: 'error.main',
                    color: 'error.main',
                    '&:hover': {
                      borderColor: 'error.dark',
                      bgcolor: 'error.light',
                    },
                  }}
                >
                  Cancel Request
                </Button>
              ) : (
                <Button
                  variant="contained"
                  size="small"
                  // startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleFollow();
                  }}
                  sx={{
                    flex: 1,
                    borderRadius: 2,
                    fontSize: '0.75rem',
                    textTransform: 'none',
                    py: 0.75,
                  }}
                >
                  Follow
                </Button>
              )}
            </>
          ) : localRequestPending || (user as UserSuggestion).hasRequestPending ? (
            <Button
              variant="outlined"
              size="small"
              fullWidth
              onClick={(e) => {
                e.stopPropagation();
                handleCancelRequest();
              }}
              sx={{
                flex: 1,
                borderRadius: 2,
                fontSize: '0.75rem',
                textTransform: 'none',
                py: 0.75,
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': {
                  borderColor: 'error.dark',
                  bgcolor: 'error.light',
                },
              }}
            >
              Cancel Request
            </Button>
          ) : (
            <Button
              variant="contained"
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={(e) => {
                e.stopPropagation();
                handleFollow();
              }}
              sx={{
                flex: 1,
                borderRadius: 2,
                fontSize: '0.75rem',
                textTransform: 'none',
                py: 0.75,
              }}
            >
              Follow
            </Button>
              )}
            </Box>
          </ListItem>
        </List>
      </CardContent>
    </Card>
  );
};